import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Star } from "lucide-react";
import { useLang, BOOKING_URL } from "../i18n";
import { EASE } from "./Reveal";

const SLIDES = [
  { src: "/assets/hero/slide1.jpg", alt: "Ampia Camera Matrimoniale con parete floreale — King's Place, Roma" },
  { src: "/assets/hero/slide2.jpg", alt: "Castel Sant'Angelo e Ponte Sant'Angelo — Roma" },
  { src: "/assets/hero/slide3.jpg", alt: "Camera Familiare — King's Place, Roma" },
  { src: "/assets/hero/slide4.jpg", alt: "Basilica di San Pietro, Via della Conciliazione — Roma" },
  { src: "/assets/hero/slide5.jpg", alt: "Corridoio d'ingresso — King's Place, Roma" },
];

const SLIDE_MS = 6500;

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block will-change-transform"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const [slide, setSlide] = useState(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);

  useEffect(() => {
    const id = setInterval(() => setSlide((s) => (s + 1) % SLIDES.length), SLIDE_MS);
    return () => clearInterval(id);
  }, []);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative min-h-screen flex flex-col overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 h-[120%]">
        <AnimatePresence>
          <motion.img
            key={slide}
            data-testid="hero-slide-image"
            src={SLIDES[slide].src}
            alt={SLIDES[slide].alt}
            initial={{ opacity: 0, scale: 1.06 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ opacity: { duration: 1.4, ease: "easeInOut" }, scale: { duration: 7, ease: "linear" } }}
            className="absolute inset-0 w-full h-full object-cover"
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F1A33]/90 via-[#0F1A33]/70 to-[#0F1A33]/40" />
        <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-[#0F1A33]/80 to-transparent" />
      </motion.div>

      <div className="relative max-w-[1400px] w-full mx-auto px-6 lg:px-12 pt-36 lg:pt-44 flex-1 flex items-center">
        <motion.div style={{ y: textY }} className="lg:max-w-[62%] relative z-10">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
            className="uppercase tracking-[0.25em] text-xs font-medium text-[#E8B54A] mb-8"
          >
            {t.hero.overline}
          </motion.p>
          <h1 className="font-serif leading-[0.92] tracking-tight text-[#F9F8F6]">
            <MaskedLine delay={0.35} className="text-[11.5vw] lg:text-[6.6vw]">{t.hero.line1}</MaskedLine>
            <MaskedLine delay={0.5} className="text-[11.5vw] lg:text-[6.6vw] italic text-[#E8B54A] leading-[1.05] pb-[0.12em] -mb-[0.12em]">{t.hero.line2}</MaskedLine>
            <MaskedLine delay={0.68} className="text-[7vw] lg:text-[3.4vw] italic font-light text-[#F9F8F6]/75 mt-4">
              {t.hero.line3}
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
            className="mt-8 max-w-md text-base md:text-lg text-[#F9F8F6]/80 leading-relaxed"
          >
            {t.hero.sub}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-5"
          >
            <a
              data-testid="hero-booking-cta"
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center rounded-full bg-[#E8B54A] text-[#14213D] px-8 py-4 text-sm font-medium tracking-wide hover:bg-[#F9F8F6] hover:scale-[1.03] transition-[background-color,transform] duration-300"
            >
              {t.hero.book}
            </a>
            <a
              data-testid="hero-availability-cta"
              href="#contatti"
              className="inline-flex items-center rounded-full border border-[#F9F8F6]/40 text-[#F9F8F6] px-8 py-4 text-sm tracking-wide hover:border-[#E8B54A] hover:text-[#E8B54A] transition-colors duration-300"
            >
              {t.hero.availability}
            </a>
            <a
              data-testid="hero-discover-cta"
              href="#camere"
              className="link-underline text-sm tracking-wide text-[#F9F8F6]"
            >
              {t.hero.discover}
            </a>
          </motion.div>
        </motion.div>
      </div>

      <div className="absolute right-6 lg:right-12 bottom-32 lg:bottom-36 z-10 flex flex-col gap-2.5">
        {SLIDES.map((s, i) => (
          <button
            key={s.src}
            data-testid={`hero-slide-dot-${i}`}
            aria-label={`Slide ${i + 1}`}
            onClick={() => setSlide(i)}
            className={`w-1.5 rounded-full transition-all duration-400 ${
              i === slide ? "h-8 bg-[#E8B54A]" : "h-1.5 bg-[#F9F8F6]/40 hover:bg-[#F9F8F6]/70"
            }`}
          />
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.3 }}
        className="relative max-w-[1400px] w-full mx-auto px-6 lg:px-12 pb-10 mt-12 z-10"
      >
        <div className="flex flex-wrap items-end justify-between gap-8 border-t border-[#F9F8F6]/20 pt-8 text-[#F9F8F6]">
          <div className="flex flex-wrap gap-10">
            {t.hero.stats.map((s) => (
              <div key={s.label} data-testid={`hero-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="flex items-center gap-2">
                  <Star size={14} className="text-[#E8B54A] fill-[#E8B54A]" />
                  <span className="font-serif text-3xl">{s.value}</span>
                </div>
                <p className="text-xs uppercase tracking-[0.15em] text-[#F9F8F6]/60 mt-1">{s.label}</p>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-3 text-[#F9F8F6]/60">
            <span className="text-xs uppercase tracking-[0.2em]">{t.hero.scroll}</span>
            <motion.span animate={{ y: [0, 6, 0] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}>
              <ArrowDown size={16} />
            </motion.span>
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
